/**
 * @description
 * Modal component for managing a user's role and assigned API key.
 *
 * @author AI Developer <ai@byteable.ai>
 * @date October 11, 2025
 */

import React, { useState, useEffect } from 'react';
import { api } from '../../services/api';
import { toast } from 'sonner';
import { ClientUser } from '../../services/api';

interface RoleAndKeyModalProps {
  isOpen: boolean;
  onClose: () => void;
  user: ClientUser | null;
  clientId: string;
  onUserUpdated: () => void;
}

interface ApiKey {
  id: string;
  service: string;
  // Add other relevant properties from your schema if needed
}

export const RoleAndKeyModal: React.FC<RoleAndKeyModalProps> = ({ isOpen, onClose, user, clientId, onUserUpdated }) => {
  const [selectedRole, setSelectedRole] = useState('');
  const [selectedApiKeyId, setSelectedApiKeyId] = useState('');
  const [availableKeys, setAvailableKeys] = useState<ApiKey[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (user) {
      setSelectedRole(user.role);
      // In a real app, you'd also fetch the user's currently assigned key
      // and set it in selectedApiKeyId.
    }

    if (clientId) {
      const fetchKeys = async () => {
        try {
          const keys = await api.getAvailableApiKeys(clientId);
          setAvailableKeys(keys);
        } catch (error) {
          toast.error('Failed to fetch available API keys.');
        }
      };
      fetchKeys();
    }
  }, [user, clientId]);

  const handleSave = async () => {
    if (!user) return;

    setIsLoading(true);
    try {
      await api.updateUserRoleAndKey(clientId, user.id, {
        role: selectedRole,
        apiKeyId: selectedApiKeyId,
      });
      toast.success('User updated successfully.');
      onUserUpdated();
      onClose();
    } catch (error) {
      toast.error('Failed to update user.');
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center">
      <div className="bg-card p-6 rounded-lg w-full max-w-md">
        <h2 className="text-lg font-medium mb-4">Edit User: {user?.userName}</h2>
        
        <div className="space-y-4">
          <div>
            <label htmlFor="role-select" className="block text-sm font-medium mb-1">Role</label>
            <select
              id="role-select"
              value={selectedRole}
              onChange={(e) => setSelectedRole(e.target.value)}
              className="w-full p-2 border rounded-md"
            >
              <option value="developer">Developer</option>
              <option value="manager">Manager</option>
              <option value="owner">Owner</option>
            </select>
          </div>

          {selectedRole === 'manager' && (
            <div>
              <label htmlFor="api-key-select" className="block text-sm font-medium mb-1">Assign API Key</label>
              <select
                id="api-key-select"
                value={selectedApiKeyId}
                onChange={(e) => setSelectedApiKeyId(e.target.value)}
                className="w-full p-2 border rounded-md"
              >
                <option value="">None</option>
                {availableKeys.map(key => (
                  <option key={key.id} value={key.id}>{`${key.service} - ${key.id.substring(0, 8)}...`}</option>
                ))}
              </select>
            </div>
          )}
        </div>

        <div className="mt-6 flex justify-end space-x-2">
          <button onClick={onClose} className="px-4 py-2 border rounded-md">Cancel</button>
          <button onClick={handleSave} className="px-4 py-2 bg-primary text-primary-foreground rounded-md" disabled={isLoading}>
            {isLoading ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
};

/*
 * © 2025 Byte Inc. All rights reserved.
 * This code is the confidential and proprietary information of Byte Inc.
 * You shall not disclose such Confidential Information and shall use it only in
 * accordance with the terms of the license agreement you entered into with Byte Inc.
 */